import { observer } from 'mobx-react-lite'
import { type IDropdownItem, Tabs } from 'shared/ui'
import type { IResponseEinItem } from 'entities/Compliance'
import { PageDescription } from 'pages/settings/ui/PageDescription/PageDescription'
import { NextButton } from 'pages/settings/ui/NextButton/NextButton'
import { CommonErrorBanner } from 'pages/settings/pages/compliance/ui/CommonErrorBanner/CommonErrorBanner'
import type { IRSEINDocumentUploaderStore } from 'pages/settings/pages/compliance/ui/IRSEINDocumentUploader'
import { AutoBusinessFind } from './AutoBusinessFind'
import { ManuallyBusinessFind } from './ManuallyBusinessFind'
import styles from './styles.module.scss'

export type IGeneralInfoStore = {
  einItem: IResponseEinItem | null
  einSearch: string
  isNoEinResults?: boolean
  einItemList: IDropdownItem[]
  errorMessage?: string
  zipCode?: string
  zipLocalErrorMessage: string | null
  isNextDisabled: boolean
  generalInfoActiveTab: number
  isUsaLocation: boolean
  states: IDropdownItem[]
  setEinSearch: (value: string) => void
  checkEinNumber: () => void
  setFirstEinItem: () => void
  addZipManually: (zip: string) => void
  checkZipLengthManually: () => void
  addCityManually: (city: string) => void
  addBusinessNameManually: (name: string) => void
  addBusinessAddressManually: (street: string) => void
  addBusinessAddress2Manually: (street: string) => void
  addBusinessStateManually: (state: string) => void
  changeGeneralInfoTab: (tabKey: number) => void
  irsEINDocumentUploaderStore: IRSEINDocumentUploaderStore
}

export type IGeneralInfoProps = {
  stepLoading: boolean
  generalInfoStore: IGeneralInfoStore
  fieldsError: Record<string, string> | null
  errorMessage?: string | null
  fieldsErrorCommonText: JSX.Element[] | null
  nextStepClick: () => void
}

export const CommonGeneralInfo = observer(
  ({
    stepLoading,
    generalInfoStore,
    fieldsError,
    errorMessage,
    fieldsErrorCommonText,
    nextStepClick,
  }: IGeneralInfoProps) => {
    const { states, generalInfoActiveTab, changeGeneralInfoTab } = generalInfoStore

    const manuallyBusinessFind = (
      <ManuallyBusinessFind
        generalInfoStore={generalInfoStore}
        fieldsError={fieldsError}
        fieldsErrorCommonText={fieldsErrorCommonText}
        states={states}
      />
    )

    return (
      <div className={styles.wrap}>
        <div className={styles.bannerWrap}>
          <PageDescription>
            Make sure this information is an exact match to your official paperwork
          </PageDescription>
          <CommonErrorBanner errorMessage={errorMessage} />
        </div>

        {generalInfoStore.isUsaLocation && (
          <>
            <Tabs
              tabs={[
                { name: 'Find your business', key: 1 },
                { name: 'Enter manually', key: 2 },
              ]}
              activeTabKey={generalInfoActiveTab}
              handleSelectTab={(tab) => changeGeneralInfoTab(tab.key)}
              margin='0 0 28px 0'
            />
            {generalInfoActiveTab === 1 && (
              <AutoBusinessFind
                generalInfoStore={generalInfoStore}
                changeGeneralInfoTab={changeGeneralInfoTab}
              />
            )}
            {generalInfoActiveTab === 2 && manuallyBusinessFind}
          </>
        )}

        {!generalInfoStore.isUsaLocation && manuallyBusinessFind}

        <NextButton
          loading={stepLoading}
          disabled={generalInfoStore.isNextDisabled || stepLoading}
          text='Next'
          onClick={nextStepClick}
        />
      </div>
    )
  }
)
